import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { sGetUser } from '../../../store/reducers/auth';
import { sGetActiveProperty } from '../../../store/reducers/properties';
import {
  GetIfInsuranceQuotes,
  BuyIfInsurance,
} from '../../../api/propertyowners';
import { IfInsuranceDocumentLink } from '../../../config';

const IfInsuranceDialog = ({
  open = false,
  logo,
  onClose,
  propertyOwner,
  activeProperty,
}) => {
  const [insuranceStep, setInsuranceStep] = React.useState(0);
  const [superQuote, setSuperQuote] = React.useState(null);
  const [disableBuy, setDisableBuy] = React.useState(false);
  const [quotesLoading, setQuotesLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [showErr, setShowErr] = React.useState(false);

  const quoteInsurance = async () => {
    const quoteRequest = {
      streetName: activeProperty.address.streetName,
      houseNumber: String(activeProperty.address.houseNumber),
      postalCode: activeProperty.address.postalCode,
      entranceCode: activeProperty.address.houseSubNumber || '',
    };

    const [err, res] = await GetIfInsuranceQuotes(quoteRequest);

    if (!err && res) {
      setSuperQuote(res);
      setQuotesLoading(false);
    } else {
      setShowErr(true);
      setDisableBuy(true);
      setError('feil vennligst kontakt If forsikring på 21 49 24 00');
    }
  };

  const handleBuyInsurance = async () => {
    setDisableBuy(true);
    const buyRequest = {
      quoteId: superQuote?.quoteId,
      policyHolderContactInformation: {
        mobilePhoneNumber: propertyOwner.mobile
          ? `+47${propertyOwner.mobile}`
          : '',
        email: propertyOwner.email,
      },
    };

    const [err, res] = await BuyIfInsurance(buyRequest);

    if (!err && res) {
      setInsuranceStep(2);
      setDisableBuy(false);
    } else {
      setShowErr(true);
      setError(
        'Det skjedde en feil. Vennligst kontakt If forsikring på 21 49 24 00'
      );
    }
  };

  const handleBuy = async () => {
    await handleBuyInsurance();
  };

  const renderDialogContent = () => {
    switch (insuranceStep) {
      case 0:
        return (
          <DialogContent className="w-full max-w-m rounded-xl p-6 shadow-lg">
            <DialogHeader>
              <DialogTitle className="text-lg font-bold">
                Bekreftelse
              </DialogTitle>
            </DialogHeader>
            <p className="text-sm text-gray-700 mt-3">
              Godtar du at Boligmappa sender ditt navn, adresse og personnummer
              til If Skadeforsirking for å gi deg et uforpliktende tilbud på
              innboforsikring?
            </p>
            <div className="flex justify-between mt-4">
              <Button
                variant="outline"
                className="rounded-lg text-gray-700 border-gray-300 w-1/2 mr-2"
                onClick={onClose}
              >
                Nei
              </Button>
              <Button
                className="rounded-lg bg-green-500 text-white hover:bg-green-600 w-1/2"
                onClick={() => {
                  quoteInsurance();
                  setInsuranceStep(1);
                }}
              >
                Ja
              </Button>
            </div>
          </DialogContent>
        );
      case 1:
        return (
          <DialogContent className="w-full max-w-m rounded-xl p-6 shadow-lg">
            <DialogHeader className="flex justify-between items-start">
              <div className="flex items-center justify-between w-full">
                <div>
                  <DialogTitle className="text-lg font-bold">
                    Ikke glem å forsikre tingene dine
                  </DialogTitle>
                  <p className="text-sm text-gray-500">
                    Supertibud fra If forsikring
                  </p>
                </div>
                <img
                  src={logo}
                  alt="IF logo"
                  width="120px"
                  className="ml-2 max-w-xm"
                />
              </div>
            </DialogHeader>
            <p className="text-sm text-gray-700 mt-3 leading-relaxed">
              Slipp å bekymre deg for det du eier i{' '}
              <span className="font-semibold"> {activeProperty.name} </span> Med
              Super innboforsikring fra If er du forsikret med ubegrenset sum.
            </p>
            <ul className="list-disc pl-5 text-sm text-gray-700 space-y-1 mt-3">
              <li>
                <span className="font-semibold">Bunadsforsikring</span> er
                inkludert
              </li>
              <li>
                <span className="font-semibold">Supergaranti</span> – føl deg
                trygg på at du har markedets beste vilkår
              </li>
              <li>
                <span className="font-semibold">10% fordelsrabatt.</span>
              </li>
            </ul>
            <Card className="p-4 bg-blue-50 rounded-lg mt-4 flex justify-between">
              {showErr ? (
                <>
                  <p className="text-sm font-medium">{error}</p>
                </>
              ) : (
                <>
                  <div>
                    <p className="text-sm font-medium">
                      {propertyOwner.fullName}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-600">Din pris er</p>
                    <p className="text-lg font-bold">
                      {quotesLoading ? (
                        <div className="flex justify-center w-full mt-2">
                          <Loader2 className="h-5 w-5 animate-spin text-gray-600" />
                        </div>
                      ) : (
                        <p className="text-lg font-bold">
                          {superQuote?.monthlyPrice} Kr/mnd
                        </p>
                      )}
                    </p>
                  </div>
                </>
              )}
            </Card>
            <div className="flex justify-between mt-4">
              <Button
                variant="outline"
                className="rounded-lg text-gray-700 border-gray-300 w-1/2 mr-2"
                onClick={onClose}
              >
                Nei takk
              </Button>
              <Button
                className="rounded-lg bg-red-500 text-white hover:bg-red-600 w-1/2"
                onClick={() => handleBuy()}
                disabled={disableBuy}
              >
                {disableBuy ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Loading...
                  </>
                ) : (
                  'Ja takk!'
                )}
              </Button>
            </div>
            <a
              href={IfInsuranceDocumentLink}
              target="_blank"
              rel="noreferrer"
              className="text-xs text-blue-500 underline block mt-3"
            >
              Les vilkår
            </a>
          </DialogContent>
        );
      case 2:
        return (
          <DialogContent className="w-full max-w-m rounded-xl p-6 shadow-lg">
            <DialogHeader className="flex justify-between items-start">
              <div className="flex items-center justify-between w-full">
                <div>
                  <DialogTitle className="text-lg font-bold">
                    Dine fordeler er nå bestilt!
                  </DialogTitle>
                  <p className="text-sm text-gray-500">
                    Supertilbud fra If forsikring
                  </p>
                </div>
                <img
                  src={logo}
                  alt="IF logo"
                  width="120px"
                  className="ml-2 max-w-xm"
                />
              </div>
            </DialogHeader>
            <p className="text-sm font-semibold mt-3">
              ... og tingene dine er nå forsikret!
            </p>
            <p className="text-sm text-gray-700 mt-2">
              Vi sender deg en bekreftelse på e-post. Du vil finne dine
              forsikringsdokumenter på Mine Sider inne på If.no innen et par
              dager.
            </p>
            <Button
              variant="outline"
              className="rounded-lg px-4 py-2 text-gray-700 border-gray-300 w-full mt-6"
              onClick={onClose}
            >
              Lukk vindu
            </Button>
          </DialogContent>
        );
      default:
        return null;
    }
  };

  useEffect(() => {
    if (open) {
      setShowErr(false);
      setDisableBuy(false);
      setInsuranceStep(0);
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      {renderDialogContent()}
    </Dialog>
  );
};

const mapStateToProps = (state) => ({
  propertyOwner: sGetUser(state),
  activeProperty: sGetActiveProperty(state),
});

export default connect(mapStateToProps)(IfInsuranceDialog);
